import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import TextArea from '../../../components/ui/TextArea';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Tooltip from '../../../components/ui/Tooltip';
import MultiSelectDropdown from '../../../components/ui/MultiSelectDropdown';
import SingleSelectDropdown from '../../../components/ui/SingleSelectDropdown';
import MultiSelectCreatable from '../../../components/ui/MultiSelectCreatable';
import ImageUpload from '../../../components/ui/ImageUpload';
import storageService from '../../../services/storageService';

const CompanyProfile = ({ companyId }) => {
  const [activeSection, setActiveSection] = useState('basics');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add this block - Missing state declarations
  const [profile, setProfile] = useState({
    logo: null,
    logoPath: null,
    banner: null,
    bannerPath: null,
    gallery: []
  });

  // Add this block - Mock user object
  const user = {
    id: getCurrentUserId()
  };

  // Mock user ID (in production, get from auth context)
  const getCurrentUserId = () => {
    let userId = localStorage.getItem('demo_user_id');
    if (!userId) {
      userId = `demo_user_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
      localStorage.setItem('demo_user_id', userId);
    }
    return userId;
  };

  // State for company asset uploads
  const [logoFiles, setLogoFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});

  // Handle logo upload
  const handleLogoUpload = useCallback(async (fileObject, uploadResult) => {
    try {
      // Update company profile with logo
      setProfile(prev => ({
        ...prev,
        logo: fileObject?.fileUrl,
        logoPath: uploadResult?.path
      }));
      setUploadErrors(prev => ({ ...prev, logo: null }));
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, logo: error?.message }));
    }
  }, []);

  // Handle banner upload
  const handleBannerUpload = useCallback(async (fileObject, uploadResult) => {
    try {
      setProfile(prev => ({
        ...prev,
        banner: fileObject?.fileUrl,
        bannerPath: uploadResult?.path
      }));
      setUploadErrors(prev => ({ ...prev, banner: null }));
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, banner: error?.message }));
    }
  }, []);

  // Handle gallery uploads
  const handleGalleryUpload = useCallback(async (fileObject, uploadResult) => {
    try {
      setProfile(prev => ({
        ...prev,
        gallery: [
          ...(prev?.gallery || []),
          {
            url: fileObject?.fileUrl,
            path: uploadResult?.path,
            name: fileObject?.name
          }
        ]
      }));
      setUploadErrors(prev => ({ ...prev, gallery: null }));
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, gallery: error?.message }));
    }
  }, []);

  // Form state - matching exactly the onboarding structure
  const [formData, setFormData] = useState({
    // Company Basics (matching StepCompanyInfo)
    name: 'Acme Corporation',
    legalName: 'Acme Corp LLC',
    websiteUrl: 'https://www.acmecorp.com',  // Changed from 'website' to 'websiteUrl'
    email: 'contact@acmecorp.com',
    industries: ['technology', 'software-development'],
    size: '51-200',
    founded: '2018',
    description: 'We are a leading technology company that specializes in innovative software solutions for businesses worldwide.',
    mission: 'To democratize technology and make powerful tools accessible to every business, regardless of size.',
    
    // Visual Assets (matching StepCompanyInfo structure)
    logo: [],  // Changed to array format like onboarding
    banner: [], // Changed to array format like onboarding  
    companyImages: [], // Changed from companyImagesPreview to companyImages
    
    // Perks & Benefits (matching StepCompanyInfo)
    perks: ['health_insurance', 'flexible_hours', 'remote_work', 'professional_development'],
    
    // Social Media (matching StepCompanyInfo structure)
    socialMedia: {
      facebook: 'https://facebook.com/acmecorp',
      linkedin: 'https://linkedin.com/company/acme-corp',
      instagram: 'https://instagram.com/acmecorp',
      twitter: 'https://x.com/acmecorp',
      tiktok: '',
      trustpilot: '',
      googleBusiness: '',
      github: 'https://github.com/acmecorp'
    }
  });

  const [errors, setErrors] = useState({});

  // Enhanced industry structure - EXACT MATCH from onboarding
  const industryCategories = [
    {
      category: 'Technology & Digital',
      industries: [
        { value: 'software-development', label: 'Software Development' },
        { value: 'artificial-intelligence', label: 'Artificial Intelligence & Machine Learning' },
        { value: 'cybersecurity', label: 'Cybersecurity' },
        { value: 'cloud-computing', label: 'Cloud Computing & DevOps' },
        { value: 'mobile-development', label: 'Mobile App Development' },
        { value: 'web-development', label: 'Web Development' },
        { value: 'data-science', label: 'Data Science & Analytics' },
        { value: 'blockchain', label: 'Blockchain & Cryptocurrency' },
        { value: 'gaming', label: 'Gaming & Interactive Entertainment' },
        { value: 'it-services', label: 'IT Services & Consulting' },
        { value: 'telecommunications', label: 'Telecommunications' },
        { value: 'e-commerce', label: 'E-commerce & Digital Platforms' }
      ]
    },
    {
      category: 'Healthcare & Life Sciences',
      industries: [
        { value: 'healthcare-services', label: 'Healthcare Services' },
        { value: 'pharmaceuticals', label: 'Pharmaceuticals' },
        { value: 'biotechnology', label: 'Biotechnology' },
        { value: 'medical-devices', label: 'Medical Devices' },
        { value: 'telemedicine', label: 'Telemedicine & Digital Health' },
        { value: 'mental-health', label: 'Mental Health Services' },
        { value: 'healthcare-technology', label: 'Healthcare Technology' },
        { value: 'clinical-research', label: 'Clinical Research' }
      ]
    },
    {
      category: 'Financial Services',
      industries: [
        { value: 'banking', label: 'Banking & Credit' },
        { value: 'investment-services', label: 'Investment & Wealth Management' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'fintech', label: 'Financial Technology (Fintech)' },
        { value: 'accounting', label: 'Accounting & Financial Advisory' },
        { value: 'cryptocurrency', label: 'Cryptocurrency & Digital Assets' },
        { value: 'payment-processing', label: 'Payment Processing' },
        { value: 'real-estate-finance', label: 'Real Estate Finance' }
      ]
    },
    {
      category: 'Manufacturing & Industrial',
      industries: [
        { value: 'automotive', label: 'Automotive' },
        { value: 'aerospace', label: 'Aerospace & Defense' },
        { value: 'manufacturing', label: 'General Manufacturing' },
        { value: 'electronics', label: 'Electronics Manufacturing' },
        { value: 'industrial-automation', label: 'Industrial Automation' },
        { value: 'chemical', label: 'Chemical Manufacturing' },
        { value: 'textiles', label: 'Textiles & Apparel Manufacturing' },
        { value: 'food-processing', label: 'Food Processing' }
      ]
    },
    {
      category: 'Energy & Environment',
      industries: [
        { value: 'renewable-energy', label: 'Renewable Energy' },
        { value: 'oil-gas', label: 'Oil & Gas' },
        { value: 'utilities', label: 'Utilities & Power Generation' },
        { value: 'environmental-services', label: 'Environmental Services' },
        { value: 'sustainability', label: 'Sustainability & Green Technology' },
        { value: 'waste-management', label: 'Waste Management' },
        { value: 'water-treatment', label: 'Water Treatment' }
      ]
    },
    {
      category: 'Retail & Consumer',
      industries: [
        { value: 'retail', label: 'Retail & Consumer Goods' },
        { value: 'fashion', label: 'Fashion & Apparel' },
        { value: 'food-beverage', label: 'Food & Beverage' },
        { value: 'luxury-goods', label: 'Luxury Goods' },
        { value: 'beauty-cosmetics', label: 'Beauty & Cosmetics' },
        { value: 'home-goods', label: 'Home & Garden' },
        { value: 'sports-recreation', label: 'Sports & Recreation' },
        { value: 'consumer-electronics', label: 'Consumer Electronics' }
      ]
    },
    {
      category: 'Media & Entertainment',
      industries: [
        { value: 'media-publishing', label: 'Media & Publishing' },
        { value: 'entertainment', label: 'Entertainment & Music' },
        { value: 'advertising', label: 'Advertising & Marketing' },
        { value: 'film-video', label: 'Film & Video Production' },
        { value: 'digital-content', label: 'Digital Content Creation' },
        { value: 'social-media', label: 'Social Media Platforms' },
        { value: 'broadcasting', label: 'Broadcasting' }
      ]
    },
    {
      category: 'Education & Research',
      industries: [
        { value: 'education', label: 'Education & Training' },
        { value: 'higher-education', label: 'Higher Education' },
        { value: 'edtech', label: 'Educational Technology' },
        { value: 'research', label: 'Research & Development' },
        { value: 'online-learning', label: 'Online Learning Platforms' },
        { value: 'corporate-training', label: 'Corporate Training' }
      ]
    },
    {
      category: 'Professional Services',
      industries: [
        { value: 'consulting', label: 'Management Consulting' },
        { value: 'legal-services', label: 'Legal Services' },
        { value: 'architecture-design', label: 'Architecture & Design' },
        { value: 'marketing-agencies', label: 'Marketing Agencies' },
        { value: 'human-resources', label: 'Human Resources Services' },
        { value: 'business-services', label: 'Business Process Services' },
        { value: 'translation', label: 'Translation & Localization' }
      ]
    },
    {
      category: 'Transportation & Logistics',
      industries: [
        { value: 'logistics', label: 'Logistics & Supply Chain' },
        { value: 'transportation', label: 'Transportation Services' },
        { value: 'shipping', label: 'Shipping & Freight' },
        { value: 'aviation', label: 'Aviation' },
        { value: 'ridesharing', label: 'Ridesharing & Mobility' },
        { value: 'delivery', label: 'Delivery Services' }
      ]
    },
    {
      category: 'Construction & Real Estate',
      industries: [
        { value: 'construction', label: 'Construction & Engineering' },
        { value: 'real-estate', label: 'Real Estate Development' },
        { value: 'property-management', label: 'Property Management' },
        { value: 'architecture-engineering', label: 'Architecture & Engineering' },
        { value: 'interior-design', label: 'Interior Design' },
        { value: 'urban-planning', label: 'Urban Planning' }
      ]
    },
    {
      category: 'Hospitality & Tourism',
      industries: [
        { value: 'hospitality', label: 'Hotels & Hospitality' },
        { value: 'restaurants', label: 'Restaurants & Food Service' },
        { value: 'travel', label: 'Travel & Tourism' },
        { value: 'events', label: 'Events & Conference Management' },
        { value: 'wellness', label: 'Wellness & Spa Services' }
      ]
    },
    {
      category: 'Agriculture & Natural Resources',
      industries: [
        { value: 'agriculture', label: 'Agriculture & Farming' },
        { value: 'forestry', label: 'Forestry & Logging' },
        { value: 'mining', label: 'Mining & Natural Resources' },
        { value: 'fisheries', label: 'Fisheries & Aquaculture' },
        { value: 'agtech', label: 'Agricultural Technology' }
      ]
    },
    {
      category: 'Government & Non-Profit',
      industries: [
        { value: 'government', label: 'Government & Public Sector' },
        { value: 'nonprofit', label: 'Non-profit Organizations' },
        { value: 'ngo', label: 'NGOs & Social Impact' },
        { value: 'public-safety', label: 'Public Safety & Emergency Services' },
        { value: 'military', label: 'Military & Defense Contractors' }
      ]
    },
    {
      category: 'Other',
      industries: [
        { value: 'other', label: 'Other Industry' }
      ]
    }
  ];

  // Flatten all industries for the multi-select
  const allIndustryOptions = industryCategories?.flatMap(category => 
    category?.industries?.map(industry => ({
      ...industry,
      category: category?.category
    }))
  );

  // Company size options - EXACT MATCH from onboarding
  const sizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1001-5000', label: '1001-5000 employees' },
    { value: '5000+', label: '5000+ employees' }
  ];

  // Helper component for label with tooltip
  const LabelWithTooltip = ({ label, tooltip, required = false }) => (
    <div className="flex items-center gap-1 mb-1">
      <span className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
      <Tooltip content={tooltip} position="top">
        <Icon 
          name="HelpCircle" 
          size={14} 
          className="text-muted-foreground hover:text-foreground cursor-help" 
        />
      </Tooltip>
    </div>
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear any previous errors for this field
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev?.socialMedia,
        [platform]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Company name is required';
    }
    
    if (!formData?.industries || formData?.industries?.length === 0) {
      newErrors.industries = 'Please select at least one industry';
    }
    
    if (!formData?.size) {
      newErrors.size = 'Please select company size';
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Company email is required';
    }
    
    if (!formData?.description?.trim()) {
      newErrors.description = 'Company description is required';
    }

    if (!formData?.mission?.trim()) {
      newErrors.mission = 'Mission and culture is required';
    }

    // Validate perks - at least one is required to match onboarding
    if (!formData?.perks || formData?.perks?.length === 0) {
      newErrors.perks = 'Please select at least one perk or benefit';
    }

    // Validate email format
    if (formData?.email?.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern?.test(formData?.email?.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // Validate website URL format if provided
    if (formData?.websiteUrl && formData?.websiteUrl?.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern?.test(formData?.websiteUrl?.trim())) {
        newErrors.websiteUrl = 'Please enter a valid website URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Mock save operation
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsEditing(false);
      // Show success message
      console.log('Profile saved successfully');
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const generateUrlSlug = (name) => {
    return name
      ?.toLowerCase()
      ?.replace(/[^a-z0-9\s-]/g, '')
      ?.replace(/\s+/g, '-')
      ?.replace(/-+/g, '-')
      ?.trim();
  };

  const sidebarSections = [
    { id: 'basics', label: 'Company Basics', icon: 'Building' },
    { id: 'media', label: 'Brand & Media', icon: 'Image' },
    { id: 'benefits', label: 'Benefits & Perks', icon: 'Gift' },
    { id: 'social', label: 'Social Media', icon: 'Share2' },
    { id: 'locations', label: 'Locations', icon: 'MapPin' }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Company Profile</h1>
          <p className="text-muted-foreground">
            Manage your company information and branding assets - same structure as onboarding
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={isSaving}
                className="flex items-center space-x-2"
              >
                <Icon name="Save" size={16} />
                <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2"
            >
              <Icon name="Edit" size={16} />
              <span>Edit Profile</span>
            </Button>
          )}
        </div>
      </div>
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card className="p-4 sticky top-6">
            <nav className="space-y-2">
              {sidebarSections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeSection === section?.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={section?.icon} size={16} />
                  <span>{section?.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Company Basics Section - MATCHING ONBOARDING STRUCTURE */}
            {activeSection === 'basics' && (
              <Card className="p-8">
                <div className="space-y-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Building" size={18} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Basic Information</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <LabelWithTooltip 
                        label="Company Name" 
                        tooltip="The official name of your company as it appears publicly"
                        required 
                      />
                      <Input
                        placeholder="Enter company name"
                        value={formData?.name}
                        onChange={(e) => handleInputChange('name', e?.target?.value)}
                        error={errors?.name}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Company URL
                      </label>
                      <div className="flex items-center">
                        <span className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-l-md border border-r-0 border-border">
                          recrutees.com/c/
                        </span>
                        <input
                          type="text"
                          readOnly
                          value={generateUrlSlug(formData?.name)}
                          className="flex-1 px-3 py-2 border border-border rounded-r-md bg-muted text-muted-foreground text-sm"
                          placeholder="company-name"
                        />
                      </div>
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Legal Name" 
                        tooltip="Official legal business name (only if different from company name)"
                      />
                      <Input
                        placeholder="Legal business name"
                        value={formData?.legalName}
                        onChange={(e) => handleInputChange('legalName', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Company Website" 
                        tooltip="Your company's official website URL"
                      />
                      <Input
                        type="url"
                        placeholder="https://www.company.com"
                        value={formData?.websiteUrl}
                        onChange={(e) => handleInputChange('websiteUrl', e?.target?.value)}
                        error={errors?.websiteUrl}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Company Email" 
                        tooltip="Primary contact email for job applications and communications"
                        required 
                      />
                      <Input
                        type="email"
                        placeholder="contact@company.com"
                        value={formData?.email}
                        onChange={(e) => handleInputChange('email', e?.target?.value)}
                        error={errors?.email}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Founded Year" 
                        tooltip="Year your company was established"
                      />
                      <Input
                        type="number"
                        placeholder="2020"
                        value={formData?.founded}
                        onChange={(e) => handleInputChange('founded', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Industries" 
                        tooltip="Select all industries that apply to your business - helps candidates find you"
                        required 
                      />
                      <MultiSelectDropdown
                        label=""
                        options={allIndustryOptions}
                        value={formData?.industries}
                        onChange={(value) => handleInputChange('industries', value)}
                        placeholder="Select multiple industries..."
                        error={errors?.industries}
                        disabled={!isEditing}
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Company Size" 
                        tooltip="Total number of employees in your company"
                        required 
                      />
                      <SingleSelectDropdown
                        options={sizeOptions}
                        value={formData?.size}
                        onChange={(value) => handleInputChange('size', value)}
                        placeholder="Select company size..."
                        error={errors?.size}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>

                  {/* Company Description - MATCHING ONBOARDING STRUCTURE */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <Icon name="FileText" size={16} className="text-primary" />
                      <h3 className="text-lg font-semibold text-foreground">About Your Company</h3>
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Company Description" 
                        tooltip="Describe what your company does, your mission, and what makes you unique (50-500 characters)"
                        required 
                      />
                      <TextArea
                        placeholder="Tell us about your company..."
                        rows={3}
                        value={formData?.description}
                        onChange={(e) => handleInputChange('description', e?.target?.value)}
                        error={errors?.description}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-muted-foreground">
                          Minimum 50 characters recommended
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formData?.description?.length || 0} characters
                        </span>
                      </div>
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Mission & Culture" 
                        tooltip="Share your company's purpose, values, and workplace culture (max 400 characters)"
                        required 
                      />
                      <TextArea
                        placeholder="Describe your mission, values, and culture..."
                        rows={3}
                        value={formData?.mission}
                        onChange={(e) => handleInputChange('mission', e?.target?.value)}
                        error={errors?.mission}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Brand & Media Section - MATCHING ONBOARDING STRUCTURE */}
            {activeSection === 'media' && (
              <Card className="p-8">
                <div className="space-y-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Image" size={18} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Company Visual Assets</h2>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Showcase Your Brand
                    </span>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Company Logo Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3">Company Logo</h3>
                        {profile?.logo ? (
                          <div className="relative inline-block">
                            <img
                              src={profile?.logo}
                              alt="Company Logo"
                              className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
                              onError={(e) => {
                                e.target.src = '/assets/images/no_image.png';
                              }}
                            />
                            <button
                              onClick={() => {
                                setProfile(prev => ({ ...prev, logo: null, logoPath: null }));
                                setLogoFiles([]);
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          </div>
                        ) : (
                          <ImageUpload
                            value={logoFiles}
                            onChange={setLogoFiles}
                            maxFiles={1}
                            maxSizeMB={5}
                            acceptedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/jpg']}
                            placeholder="Upload company logo"
                            bucketName={storageService?.STORAGE_BUCKETS?.COMPANY_ASSETS}
                            userId={user?.id}
                            autoUpload={true}
                            onUploadComplete={handleLogoUpload}
                            onUploadError={(f, e) => setUploadErrors(prev => ({ ...prev, logo: e?.message }))}
                            preview={true}
                          />
                        )}
                        {uploadErrors?.logo && (
                          <p className="text-sm text-red-500 mt-2">{uploadErrors?.logo}</p>
                        )}
                      </div>

                      {/* Company Banner Section */}
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3">Company Banner</h3>
                        {profile?.banner ? (
                          <div className="relative">
                            <img
                              src={profile?.banner}
                              alt="Company Banner"
                              className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                              onError={(e) => {
                                e.target.src = '/assets/images/no_image.png';
                              }}
                            />
                            <button
                              onClick={() => {
                                setProfile(prev => ({ ...prev, banner: null, bannerPath: null }));
                                setBannerFiles([]);
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          </div>
                        ) : (
                          <ImageUpload
                            value={bannerFiles}
                            onChange={setBannerFiles}
                            maxFiles={1}
                            maxSizeMB={10}
                            acceptedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/jpg']}
                            placeholder="Upload company banner (recommended: 1920x400px)"
                            bucketName={storageService?.STORAGE_BUCKETS?.COMPANY_ASSETS}
                            userId={user?.id}
                            autoUpload={true}
                            onUploadComplete={handleBannerUpload}
                            onUploadError={(f, e) => setUploadErrors(prev => ({ ...prev, banner: e?.message }))}
                            preview={true}
                          />
                        )}
                        {uploadErrors?.banner && (
                          <p className="text-sm text-red-500 mt-2">{uploadErrors?.banner}</p>
                        )}
                      </div>
                    </div>

                    {/* Company Gallery Section */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">Company Gallery</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload images showcasing your office, team, and work environment (max 10 images)
                      </p>
                      
                      {/* Existing gallery images */}
                      {profile?.gallery?.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          {profile?.gallery?.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image?.url}
                                alt={`Gallery ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                                onError={(e) => {
                                  e.target.src = '/assets/images/no_image.png';
                                }}
                              />
                              <button
                                onClick={async () => {
                                  // Delete from storage
                                  if (image?.path) {
                                    try {
                                      await storageService?.deleteFile(image?.path, storageService?.STORAGE_BUCKETS?.COMPANY_ASSETS);
                                    } catch (error) {
                                      console.error('Failed to delete gallery image:', error);
                                    }
                                  }
                                  // Remove from state
                                  setProfile(prev => ({
                                    ...prev,
                                    gallery: prev?.gallery?.filter((_, i) => i !== index)
                                  }));
                                }}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              >
                                <Icon name="X" size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Gallery upload */}
                      {(!profile?.gallery || profile?.gallery?.length < 10) && (
                        <ImageUpload
                          value={galleryFiles}
                          onChange={setGalleryFiles}
                          maxFiles={10 - (profile?.gallery?.length || 0)}
                          maxSizeMB={10}
                          acceptedTypes={['image/jpeg', 'image/png', 'image/webp', 'image/jpg']}
                          placeholder="Add gallery images"
                          bucketName={storageService?.STORAGE_BUCKETS?.COMPANY_ASSETS}
                          userId={user?.id}
                          autoUpload={true}
                          onUploadComplete={handleGalleryUpload}
                          onUploadError={(f, e) => setUploadErrors(prev => ({ ...prev, gallery: e?.message }))}
                          preview={true}
                          dragAndDrop={true}
                        />
                      )}
                      {uploadErrors?.gallery && (
                        <p className="text-sm text-red-500 mt-2">{uploadErrors?.gallery}</p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Benefits & Perks Section - MATCHING ONBOARDING STRUCTURE */}
            {activeSection === 'benefits' && (
              <Card className="p-8">
                <div className="space-y-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Gift" size={18} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Perks & Benefits</h2>
                  </div>

                  <div>
                    <LabelWithTooltip 
                      label="Company Perks & Benefits" 
                      tooltip="Select all perks and benefits your company provides to attract candidates"
                      required 
                    />
                    <MultiSelectCreatable
                      userId={getCurrentUserId()}
                      fieldKey="company_perks_benefits"
                      options={[
                        { value: 'health_insurance', label: 'Health Insurance' },
                        { value: 'dental_insurance', label: 'Dental Insurance' },
                        { value: 'vision_insurance', label: 'Vision Insurance' },
                        { value: 'life_insurance', label: 'Life Insurance' },
                        { value: '401k_matching', label: '401(k) Matching' },
                        { value: 'flexible_hours', label: 'Flexible Working Hours' },
                        { value: 'remote_work', label: 'Remote Work Options' },
                        { value: 'hybrid_work', label: 'Hybrid Work Model' },
                        { value: 'unlimited_pto', label: 'Unlimited PTO' },
                        { value: 'paid_vacation', label: 'Paid Vacation' },
                        { value: 'sick_leave', label: 'Paid Sick Leave' },
                        { value: 'parental_leave', label: 'Parental Leave' },
                        { value: 'sabbatical', label: 'Sabbatical Program' },
                        { value: 'professional_development', label: 'Professional Development' },
                        { value: 'tuition_reimbursement', label: 'Tuition Reimbursement' },
                        { value: 'conference_attendance', label: 'Conference Attendance' },
                        { value: 'training_budget', label: 'Training Budget' },
                        { value: 'gym_membership', label: 'Gym Membership' },
                        { value: 'wellness_program', label: 'Wellness Programs' },
                        { value: 'mental_health', label: 'Mental Health Support' },
                        { value: 'free_lunch', label: 'Free Lunch/Meals' },
                        { value: 'snacks_drinks', label: 'Free Snacks & Drinks' },
                        { value: 'catered_meals', label: 'Catered Meals' },
                        { value: 'commuter_benefits', label: 'Commuter Benefits' },
                        { value: 'parking', label: 'Free Parking' },
                        { value: 'transit_pass', label: 'Transit Pass' },
                        { value: 'bike_storage', label: 'Bike Storage' },
                        { value: 'childcare', label: 'Childcare Support' },
                        { value: 'pet_friendly', label: 'Pet-Friendly Office' },
                        { value: 'game_room', label: 'Game Room/Recreation Area' },
                        { value: 'standing_desk', label: 'Standing Desks' },
                        { value: 'ergonomic_furniture', label: 'Ergonomic Furniture' },
                        { value: 'tech_stipend', label: 'Technology Stipend' },
                        { value: 'home_office', label: 'Home Office Setup' },
                        { value: 'phone_reimbursement', label: 'Phone/Internet Reimbursement' },
                        { value: 'stock_options', label: 'Stock Options/Equity' },
                        { value: 'bonus_program', label: 'Performance Bonuses' },
                        { value: 'profit_sharing', label: 'Profit Sharing' },
                        { value: 'team_events', label: 'Team Building Events' },
                        { value: 'company_retreats', label: 'Company Retreats' },
                        { value: 'volunteer_time', label: 'Volunteer Time Off' },
                        { value: 'diversity_programs', label: 'Diversity & Inclusion Programs' }
                      ]}
                      value={Array.isArray(formData?.perks) ? formData?.perks : []}
                      onChange={(value) => handleInputChange('perks', value)}
                      placeholder="Select multiple perks and benefits..."
                      allowCreate={true}
                      disabled={!isEditing}
                      validateNewOption={(raw) => {
                        if (!raw?.trim()) return 'Benefit name cannot be empty';
                        if (raw?.trim()?.length < 2) return 'Benefit name must be at least 2 characters';
                        if (raw?.trim()?.length > 50) return 'Benefit name must be less than 50 characters';
                        return null;
                      }}
                      normalizeNewOption={(raw) => {
                        return raw?.trim()
                          ?.replace(/\s+/g, ' ')
                          ?.replace(/\w\S*/g, (txt) => 
                            txt?.charAt(0)?.toUpperCase() + txt?.substr(1)?.toLowerCase()
                          );
                      }}
                      computeNewValue={(label) => {
                        return label
                          ?.toLowerCase()
                          ?.replace(/[^a-z0-9\s]/g, '')
                          ?.replace(/\s+/g, '_')
                          ?.replace(/^_+|_+$/g, '');
                      }}
                      error={errors?.perks}
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      These benefits will be displayed on your job postings and company profile
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Social Media Section - MATCHING ONBOARDING STRUCTURE */}
            {activeSection === 'social' && (
              <Card className="p-8">
                <div className="space-y-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="Share2" size={18} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Social Media</h2>
                    <span className="text-xs text-muted-foreground">(Optional)</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <LabelWithTooltip 
                        label="Facebook" 
                        tooltip="Your company's Facebook page for community engagement"
                      />
                      <Input
                        placeholder="https://facebook.com/company"
                        value={formData?.socialMedia?.facebook}
                        onChange={(e) => handleSocialMediaChange('facebook', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="LinkedIn" 
                        tooltip="Your company's LinkedIn profile for professional networking"
                      />
                      <Input
                        placeholder="https://linkedin.com/company/name"
                        value={formData?.socialMedia?.linkedin}
                        onChange={(e) => handleSocialMediaChange('linkedin', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Instagram" 
                        tooltip="Your company's Instagram account for visual content and brand awareness"
                      />
                      <Input
                        placeholder="https://instagram.com/company"
                        value={formData?.socialMedia?.instagram}
                        onChange={(e) => handleSocialMediaChange('instagram', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="X (Formerly Known as Twitter)" 
                        tooltip="Your company's X/Twitter account for updates and announcements"
                      />
                      <Input
                        placeholder="https://x.com/company or @company"
                        value={formData?.socialMedia?.twitter}
                        onChange={(e) => handleSocialMediaChange('twitter', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="TikTok" 
                        tooltip="Your company's TikTok account for creative content and youth engagement"
                      />
                      <Input
                        placeholder="https://tiktok.com/@company"
                        value={formData?.socialMedia?.tiktok}
                        onChange={(e) => handleSocialMediaChange('tiktok', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="TrustPilot" 
                        tooltip="Your company's Trustpilot profile for customer reviews and trust building"
                      />
                      <Input
                        placeholder="https://trustpilot.com/review/company.com"
                        value={formData?.socialMedia?.trustpilot}
                        onChange={(e) => handleSocialMediaChange('trustpilot', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="Google Business" 
                        tooltip="Your company's Google Business profile for local search and reviews"
                      />
                      <Input
                        placeholder="https://business.google.com/company"
                        value={formData?.socialMedia?.googleBusiness}
                        onChange={(e) => handleSocialMediaChange('googleBusiness', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>

                    <div>
                      <LabelWithTooltip 
                        label="GitHub" 
                        tooltip="Your company's GitHub organization for open source projects"
                      />
                      <Input
                        placeholder="https://github.com/company"
                        value={formData?.socialMedia?.github}
                        onChange={(e) => handleSocialMediaChange('github', e?.target?.value)}
                        disabled={!isEditing}
                        className="border-2 border-gray-200 focus:border-primary"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Locations Section - NEW ADDITION AFTER SOCIAL MEDIA */}
            {activeSection === 'locations' && (
              <Card className="p-8">
                <div className="space-y-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="MapPin" size={18} className="text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Company Locations</h2>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                    <Icon name="MapPin" size={48} className="text-blue-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Manage Your Locations
                    </h3>
                    <p className="text-blue-700 mb-4">
                      For comprehensive location management including creation, editing, and detailed configuration, 
                      please use the dedicated Locations section in your dashboard sidebar.
                    </p>
                    <Button
                      onClick={() => window.location.href = `/company-dashboard-hub/${companyId}/locations`}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Icon name="ExternalLink" size={16} className="mr-2" />
                      Go to Locations Management
                    </Button>
                  </div>

                  {/* Quick Overview of Current Locations */}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Current Locations Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Mock locations preview */}
                      <div className="border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon name="Building2" size={16} className="text-primary" />
                          <span className="font-medium">San Francisco Headquarters</span>
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">HQ</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          123 Tech Street, San Francisco, CA 94105
                        </p>
                      </div>
                      
                      <div className="border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon name="Building2" size={16} className="text-primary" />
                          <span className="font-medium">New York Office</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          456 Business Ave, New York, NY 10001
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;